import React, { useState, useEffect, useRef } from "react";
import SideBar from "../components/SideBar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/EditCustomerProfile.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import { agentService } from "../services/AgentServices";

function EditAgentProfile() {
  const [step, setStep] = useState(1);
  const location = useLocation();
  const agent = location.state?.agent || {};
  const navigate = useNavigate();

  // State initialization with actual API data structure
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [addressLine3, setAddressLine3] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Admin Area States
  const [assignedAreas, setAssignedAreas] = useState([]);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [counties, setCounties] = useState([]);
  const [areaFilters, setAreaFilters] = useState({
    county_id: "",
    search: "",
  });
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);

  // Validation states
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [line1Error, setLine1Error] = useState("");
  const [cityError, setCityError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");

  // Email validation states
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState("");

  // Phone validation states
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(null);
  const [phoneCheckMessage, setPhoneCheckMessage] = useState("");

  // Store original values for comparison
  const [originalEmail, setOriginalEmail] = useState("");
  const [originalPhone, setOriginalPhone] = useState("");
  const [originalPostalCode, setOriginalPostalCode] = useState("");

  // Check if agent is Senior Business Ads Agent
  const isSeniorAgent = agent.role === "SeniorBusinessAdsAgent";

  // Initialize form with agent data
  useEffect(() => {
    if (agent) {
      const agentEmail = agent.email || "";
      const agentPhone = agent.phone_number || agent.mobile || "";

      setFirstName(agent.first_name || agent.firstName || "");
      setLastName(agent.last_name || agent.surname || "");
      setEmail(agentEmail);
      setPhoneNumber(agentPhone);
      setAddressLine1(agent.address_line1 || agent.address?.line1 || "");
      setAddressLine2(agent.address_line2 || agent.address?.line2 || "");
      setAddressLine3(agent.address_line3 || agent.address?.line3 || "");
      setCity(agent.city || agent.address?.city || "");
      setPostalCode(
        agent.postcode || agent.address?.postcode || agent.postalCode || "",
      );
      setRegion(agent.region || agent.address?.county || "");
      setCountry(agent.country || agent.address?.country || "");
      setNote(agent.status_note || agent.notes || agent.note || "");

      // Store original values
      setOriginalEmail(agentEmail);
      setOriginalPhone(agentPhone);
      setOriginalPostalCode(
        agent.postcode || agent.address?.postcode || agent.postalCode || "",
      );

      // Set status with proper formatting
      const currentStatus = agent.current_status || agent.status;
      if (currentStatus) {
        setStatus({
          value: currentStatus,
          label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1),
        });
      }
    }
  }, [agent]);

  // Load admin areas when step 4 becomes active
  useEffect(() => {
    if (step === 4 && isSeniorAgent) {
      fetchCounties();
      fetchAssignedAreas();
      fetchAvailableAreas();
    }
  }, [step]);

  // Debounce search filter for admin areas
  useEffect(() => {
    if (step === 4 && isSeniorAgent) {
      const timer = setTimeout(() => {
        fetchAvailableAreas();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [areaFilters, step]);

  // Fetch counties for filter
  const fetchCounties = async () => {
    try {
      const response = await agentService.getCounties();
      if (response.success) {
        setCounties(
          response.data.map((county) => ({
            value: county.county_id,
            label: county.county_name,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching counties:", error);
    }
  };

  // Fetch assigned admin areas
  const fetchAssignedAreas = async () => {
    try {
      const response = await agentService.getAgentAdminAreas(
        agent.agent_id || agent.id,
      );
      if (response.success) {
        setAssignedAreas(response.data.assigned_admin_areas);
      }
    } catch (error) {
      console.error("Error fetching assigned areas:", error);
    }
  };

  // Fetch available admin areas
  const fetchAvailableAreas = async () => {
    setLoadingAreas(true);
    try {
      const params = {
        ...areaFilters,
        include_assignments: true,
        agent_id: agent.agent_id || agent.id,
        per_page: 100,
      };

      if (areaFilters.county_id === "") delete params.county_id;
      if (areaFilters.search === "") delete params.search;

      const response = await agentService.getAvailableAdminAreas(params);
      if (response.success) {
        setAvailableAreas(response.data.admin_areas);
      }
    } catch (error) {
      console.error("Error fetching available areas:", error);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Toggle area selection
  const toggleAreaSelection = (area) => {
    if (selectedAreas.some((a) => a.admin_area_id === area.admin_area_id)) {
      setSelectedAreas(
        selectedAreas.filter((a) => a.admin_area_id !== area.admin_area_id),
      );
    } else {
      setSelectedAreas([...selectedAreas, area]);
    }
  };

  // Select all visible areas
  const selectAllVisible = () => {
    const unassignedAreas = availableAreas.filter((area) => !area.is_assigned);
    setSelectedAreas(unassignedAreas);
  };

  // Clear selections
  const clearSelections = () => {
    setSelectedAreas([]);
  };

  // Handle assign areas
  const handleAssignAreas = async () => {
    if (selectedAreas.length === 0) {
      setError("Please select at least one admin area");
      return;
    }

    setLoading(true);
    try {
      const response = await agentService.assignAdminAreas(
        agent.agent_id || agent.id,
        selectedAreas.map((area) => area.admin_area_id),
        replaceExisting,
      );

      if (response.success) {
        // Refresh assigned areas
        await fetchAssignedAreas();
        await fetchAvailableAreas();
        setSelectedAreas([]);
        setError("");
        alert("Admin areas assigned successfully!");
      }
    } catch (error) {
      console.error("Error assigning areas:", error);
      setError(error.message || "Failed to assign admin areas");
    } finally {
      setLoading(false);
    }
  };

  // Function to get phone digit count
  const getPhoneDigitCount = () => {
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
    let nationalNumber = digitsOnly;
    if (match) {
      nationalNumber = digitsOnly.slice(match[0].length);
    }
    return nationalNumber.length;
  };

  const hasValidPhoneDigits = getPhoneDigitCount() >= 9;

  // Check email and phone availability simultaneously
  const checkEmailAndPhoneAvailability = async () => {
    let emailValid = true;
    let phoneValid = true;

    // Check email format (only if changed)
    if (email !== originalEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        emailValid = false;
      }
    }

    // Check phone format (only if changed)
    if (phoneNumber !== originalPhone) {
      const digitsOnly = phoneNumber.replace(/\D/g, "");
      const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
      let nationalNumber = digitsOnly;
      if (match) {
        nationalNumber = digitsOnly.slice(match[0].length);
      }
      if (nationalNumber.length !== 10) {
        phoneValid = false;
      }
    }

    // If neither changed, no need to check
    if (email === originalEmail && phoneNumber === originalPhone) {
      return { emailAvailable: true, phoneAvailable: true };
    }

    // Set both to checking state if needed
    if (email !== originalEmail) {
      setEmailChecking(true);
      setEmailAvailable(null);
      setEmailCheckMessage("Checking email availability...");
    }

    if (phoneNumber !== originalPhone) {
      setPhoneChecking(true);
      setPhoneAvailable(null);
      setPhoneCheckMessage("Checking phone availability...");
    }

    try {
      // Prepare API calls for changed fields only
      const apiCalls = [];

      if (email !== originalEmail && emailValid) {
        apiCalls.push(agentService.checkEmailAvailability(email));
      }

      if (phoneNumber !== originalPhone && phoneValid) {
        apiCalls.push(agentService.checkPhoneAvailability(phoneNumber));
      }

      // Make API calls simultaneously
      const results = await Promise.all(apiCalls);

      // Process results
      let emailResult = { available: true, message: "" };
      let phoneResult = { available: true, message: "" };

      if (email !== originalEmail && emailValid) {
        emailResult = results.shift();
        setEmailAvailable(emailResult.available);
        setEmailCheckMessage(emailResult.message);
      }

      if (phoneNumber !== originalPhone && phoneValid) {
        phoneResult = results.shift();
        setPhoneAvailable(phoneResult.available);
        setPhoneCheckMessage(phoneResult.message);
      }

      return {
        emailAvailable: emailResult.available,
        phoneAvailable: phoneResult.available,
      };
    } catch (error) {
      console.error("Availability check error:", error);

      if (email !== originalEmail) {
        setEmailAvailable(null);
        setEmailCheckMessage("Error checking email availability");
      }

      if (phoneNumber !== originalPhone) {
        setPhoneAvailable(null);
        setPhoneCheckMessage("Error checking phone availability");
      }

      return {
        emailAvailable: false,
        phoneAvailable: false,
      };
    } finally {
      setEmailChecking(false);
      setPhoneChecking(false);
    }
  };

  const nextStep = async () => {
    if (step === 1) {
      const isValid = validateBasicDetails();
      if (!isValid) return;

      const availability = await checkEmailAndPhoneAvailability();

      if (email !== originalEmail) {
        if (availability.emailAvailable === false) {
          setEmailError("");
          return;
        }
        if (availability.emailAvailable === null) {
          setEmailError(
            "Unable to verify email availability. Please try again.",
          );
          return;
        }
      }

      if (phoneNumber !== originalPhone) {
        if (availability.phoneAvailable === false) {
          setPhoneError("");
          return;
        }
        if (availability.phoneAvailable === null) {
          setPhoneError(
            "Unable to verify phone availability. Please try again.",
          );
          return;
        }
      }

      if (
        (email === originalEmail || availability.emailAvailable === true) &&
        (phoneNumber === originalPhone || availability.phoneAvailable === true)
      ) {
        setStep((prev) => Math.min(prev + 1, isSeniorAgent ? 4 : 3));
      }
    } else if (step === 2) {
      const isValid = validateAddressDetails();
      if (!isValid) return;
      setStep((prev) => Math.min(prev + 1, isSeniorAgent ? 4 : 3));
    } else if (step === 3) {
      if (isSeniorAgent) {
        setStep(4);
      } else {
        await handleUpdate();
      }
    } else if (step === 4) {
      await handleAssignAreas();
      await handleUpdate();
    }
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleStepClick = (stepNumber) => {
    if (stepNumber === step) return;
    setStep(stepNumber);
  };

  const steps = [
    { label: "Basic details", sub: "Enter your basic details" },
    { label: "Address", sub: "Enter your address details" },
    { label: "Status", sub: "Update the status" },
  ];

  // Add Admin Areas step only for senior agents
  if (isSeniorAgent) {
    steps.push({ label: "Admin Areas", sub: "Manage assigned areas" });
  }

  const regionOptions = {
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",
      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",
      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",
      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
  };

  const statusOptions = [
    { value: "approved", label: "Approved" },
    { value: "awaiting", label: "Awaiting" },
    { value: "suspended", label: "Suspended" },
    { value: "rejected", label: "Rejected" },
  ];

  const validateBasicDetails = () => {
    let valid = true;

    if (!firstName.trim()) {
      setFirstNameError("First name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
      setFirstNameError("First name must contain only letters.");
      valid = false;
    } else if (firstName.trim().length < 3) {
      setFirstNameError("First name must be at least 3 letters long.");
      valid = false;
    } else {
      setFirstNameError("");
    }

    if (!lastName.trim()) {
      setLastNameError("Last name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
      setLastNameError("Last name must contain only letters.");
      valid = false;
    } else if (lastName.trim().length < 3) {
      setLastNameError("Last name must be at least 3 letters long.");
      valid = false;
    } else {
      setLastNameError("");
    }

    if (!email.trim()) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      if (!emailError.includes("already registered")) {
        setEmailError("");
      }
    }

    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required");
      valid = false;
    } else {
      const digitsOnly = phoneNumber.replace(/\D/g, "");
      const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
      let nationalNumber = digitsOnly;
      if (match) {
        nationalNumber = digitsOnly.slice(match[0].length);
      }

      if (nationalNumber.length > 10) {
        setPhoneError(
          "Phone number must not exceed 10 digits (excluding country code).",
        );
        valid = false;
      } else {
        if (!phoneError.includes("already registered")) {
          setPhoneError("");
        }
      }
    }

    return valid;
  };

  const validateAddressDetails = () => {
    let valid = true;

    if (!addressLine1.trim()) {
      setLine1Error("Address line 1 is required.");
      valid = false;
    } else {
      setLine1Error("");
    }

    if (!city.trim()) {
      setCityError("City is required.");
      valid = false;
    } else {
      setCityError("");
    }

    if (!postalCode.trim()) {
      setPostalCodeError("Postal code is required.");
      valid = false;
    } else if (!/^[A-Za-z0-9\s\-]+$/.test(postalCode)) {
      setPostalCodeError("Please enter a valid postal code.");
      valid = false;
    } else {
      setPostalCodeError("");
    }

    return valid;
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError("");

    try {
      const updateData = {
        first_name: firstName,
        last_name: lastName,
        ...(email !== originalEmail && { email: email }),
        ...(phoneNumber !== originalPhone && { phone_number: phoneNumber }),
        address_line1: addressLine1,
        address_line2: addressLine2,
        address_line3: addressLine3,
        city: city,
        postcode: postalCode,
        country_name: country,
        region: region,
      };

      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined || updateData[key] === null) {
          delete updateData[key];
        }
      });

      console.log("Updating agent with data:", updateData);

      await agentService.updateagent(agent.agent_id || agent.id, updateData);

      const currentStatus = agent.current_status || agent.status;
      if (status && status.value !== currentStatus) {
        await agentService.updateagentStatus(agent.agent_id || agent.id, {
          user_status: status.value,
          notes: note || `Status changed to ${status.value} by admin`,
        });
      }

      const updatedagent = {
        ...agent,
        firstName: firstName,
        surname: lastName,
        first_name: firstName,
        last_name: lastName,
        email: email,
        mobile: phoneNumber,
        phone_number: phoneNumber,
        address_line1: addressLine1,
        address_line2: addressLine2,
        address_line3: addressLine3,
        city: city,
        postcode: postalCode,
        region: region,
        country: country,
        address: {
          line1: addressLine1,
          line2: addressLine2,
          line3: addressLine3,
          city: city,
          postcode: postalCode,
          county: region,
          country: country,
        },
        current_status: status?.value || currentStatus,
        status: status?.value || currentStatus,
        status_note: note,
        notes: note || agent.notes,
      };

      navigate("/agent-details", {
        state: {
          agent: updatedagent,
          message: "Agent profile updated successfully!",
        },
      });
    } catch (err) {
      console.error("Error updating agent:", err);

      if (err.response?.data?.errors?.email) {
        setError(
          "Email address is already taken. Please use a different email.",
        );
      } else if (err.response?.data?.errors?.phone_number) {
        setError(
          "Phone number is already registered. Please use a different phone number.",
        );
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to update agent profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFirstName(agent.first_name || agent.firstName || "");
    setLastName(agent.last_name || agent.surname || "");
    setEmail(originalEmail);
    setPhoneNumber(originalPhone);
    setAddressLine1(agent.address_line1 || agent.address?.line1 || "");
    setAddressLine2(agent.address_line2 || agent.address?.line2 || "");
    setAddressLine3(agent.address_line3 || agent.address?.line3 || "");
    setCity(agent.city || agent.address?.city || "");
    setPostalCode(originalPostalCode);
    setRegion(agent.region || agent.address?.county || "");
    setCountry(agent.country || agent.address?.country || "");
    setNote(agent.status_note || agent.notes || agent.note || "");

    const currentStatus = agent.current_status || agent.status;
    if (currentStatus) {
      setStatus({
        value: currentStatus,
        label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1),
      });
    } else {
      setStatus(null);
    }

    setFirstNameError("");
    setLastNameError("");
    setEmailError("");
    setPhoneError("");
    setLine1Error("");
    setCityError("");
    setPostalCodeError("");
    setError("");

    setEmailAvailable(null);
    setEmailCheckMessage("");
    setPhoneAvailable(null);
    setPhoneCheckMessage("");

    setSelectedAreas([]);
    setStep(1);
  };

  const currentCountryOption = country
    ? { value: country, label: country }
    : null;
  const currentRegionOption = region ? { value: region, label: region } : null;

  return (
    <div className="member-details-layout">
      <SideBar />

      <div className="member-details-main">
        <div className="edit-header">
          <h2>Edit Profile</h2>
          <p>You can edit your details to update your profile</p>
        </div>

        <div className="breadcrumb">
          <Link to="/manage-agent" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <Link to="/manage-agent" className="breadcrumb-link">
            Manage Retail Advertising Business Agent
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-current">Edit Profile</span>
        </div>

        {error && (
          <div
            className="error-message"
            style={{
              margin: "1rem 0",
              padding: "0.75rem",
              background: "#f8d7da",
              color: "#721c24",
              borderRadius: "4px",
            }}
          >
            {error}
          </div>
        )}

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <button
                    type="button"
                    className={`circle ${
                      isCompleted ? "completed" : isActive ? "active" : ""
                    } clickable`}
                    onClick={() => handleStepClick(stepNum)}
                    disabled={isActive}
                    title={`Go to Step ${stepNum}: ${item.label}`}
                  >
                    {isCompleted ? "✓" : stepNum}
                  </button>
                  <div className="step-text">
                    <p
                      className={`step-label ${isActive ? "active-label" : ""}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`step-subtext ${isActive ? "active-sub" : ""}`}
                    >
                      {item.sub}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`line ${
                        isCompleted ? "line-filled" : "line-empty"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="step-content">
            {step === 1 && (
              <div>
                <h3>Basic Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      First name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (firstNameError) setFirstNameError("");
                      }}
                      placeholder="Enter first name"
                    />
                    <div className="error-container">
                      {firstNameError && (
                        <p className="error-text">{firstNameError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Last name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (lastNameError) setLastNameError("");
                      }}
                      placeholder="Enter last name"
                    />
                    <div className="error-container">
                      {lastNameError && (
                        <p className="error-text">{lastNameError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      disabled
                      value={email}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEmail(value);
                        setEmailAvailable(null);
                        setEmailCheckMessage("");
                        if (
                          emailError &&
                          !emailError.includes("already registered")
                        ) {
                          setEmailError("");
                        }
                      }}
                      placeholder="Enter email"
                      className={
                        email !== originalEmail
                          ? emailAvailable === false
                            ? "email-taken"
                            : emailAvailable === true
                              ? "email-available"
                              : ""
                          : ""
                      }
                    />

                    {email !== originalEmail && (
                      <>
                        {emailChecking && (
                          <div className="email-status checking">
                            Checking email availability...
                          </div>
                        )}

                        {!emailChecking && emailCheckMessage && (
                          <div
                            className={`email-status ${emailAvailable === true ? "available" : emailAvailable === false ? "taken" : ""}`}
                          >
                            {emailCheckMessage}
                          </div>
                        )}
                      </>
                    )}

                    <div className="error-container">
                      {emailError && <p className="error-text">{emailError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Phone Number <span className="required">*</span>
                    </label>
                    <div
                      className={`phone-input-container ${phoneNumber !== originalPhone ? (phoneAvailable === false ? "phone-taken" : phoneAvailable === true ? "phone-available" : "") : ""}`}
                    >
                      <PhoneInput
                        country={"gb"}
                        value={phoneNumber}
                        onChange={(value, country) => {
                          const digitsOnly = value.replace(/\D/g, "");
                          const dialCode = country?.dialCode || "";
                          const dialCodeLength = dialCode.length;
                          let nationalNumber = digitsOnly.slice(dialCodeLength);

                          if (nationalNumber.length > 9) {
                            nationalNumber = nationalNumber.slice(0, 9);
                          }

                          const finalValue = `+${dialCode}${nationalNumber}`;
                          setPhoneNumber(finalValue);

                          setPhoneAvailable(null);
                          setPhoneCheckMessage("");

                          if (
                            phoneError &&
                            !phoneError.includes("already registered")
                          ) {
                            setPhoneError("");
                          }
                        }}
                        onKeyDown={(e) => {
                          const digitsOnly = phoneNumber.replace(/\D/g, "");
                          const dialCode =
                            phoneNumber.match(/^\+(\d{1,3})/)?.[1] || "";
                          const nationalNumber = digitsOnly.slice(
                            dialCode.length,
                          );

                          if (
                            nationalNumber.length >= 9 &&
                            /[0-9]/.test(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        inputProps={{
                          name: "phone_number",
                          required: true,
                        }}
                        inputClass="phone-input"
                        containerClass="phone-container"
                      />
                    </div>

                    {phoneNumber !== originalPhone && (
                      <>
                        {phoneChecking && (
                          <div className="phone-status checking">
                            Checking phone availability...
                          </div>
                        )}

                        {!phoneChecking && phoneCheckMessage && (
                          <div
                            className={`phone-status ${phoneAvailable === true ? "available" : phoneAvailable === false ? "taken" : ""}`}
                          >
                            {phoneCheckMessage}
                          </div>
                        )}
                      </>
                    )}

                    <div className="error-container">
                      {phoneError && <p className="error-text">{phoneError}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3>Address</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Address line 1 <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={addressLine1}
                      onChange={(e) => {
                        setAddressLine1(e.target.value);
                        if (line1Error) setLine1Error("");
                      }}
                      placeholder="Enter address line 1"
                    />
                    <div className="error-container">
                      {line1Error && <p className="error-text">{line1Error}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address line 2</label>
                    <input
                      type="text"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Enter address line 2"
                    />
                    <div className="error-container" />
                  </div>

                  <div className="form-group full-width">
                    <label>Address line 3</label>
                    <input
                      type="text"
                      value={addressLine3}
                      onChange={(e) => setAddressLine3(e.target.value)}
                      placeholder="Enter address line 3"
                    />
                    <div className="error-container" />
                  </div>

                  <div className="form-group">
                    <label>
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        if (cityError) setCityError("");
                      }}
                      placeholder="Enter city"
                    />
                    <div className="error-container">
                      {cityError && <p className="error-text">{cityError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Region <span className="required">*</span>
                    </label>
                    <Select
                      options={
                        country
                          ? regionOptions[country]?.map((r) => ({
                              value: r,
                              label: r,
                            })) || []
                          : []
                      }
                      value={currentRegionOption}
                      onChange={(option) => setRegion(option.value)}
                      placeholder="Select or type region"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>

                  <div className="form-group">
                    <label>
                      Post Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => {
                        setPostalCode(e.target.value);
                        if (postalCodeError) setPostalCodeError("");
                      }}
                      placeholder="Enter postal code"
                    />
                    <div className="error-container">
                      {postalCodeError && (
                        <p className="error-text">{postalCodeError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Country <span className="required">*</span>
                    </label>
                    <Select
                      options={Object.keys(regionOptions).map((c) => ({
                        value: c,
                        label: c,
                      }))}
                      value={currentCountryOption}
                      onChange={(option) => {
                        setCountry(option.value);
                        setRegion("");
                        const regions = regionOptions[option.value];
                        if (regions && regions.length > 0) {
                          setRegion(regions[0]);
                        }
                      }}
                      placeholder="Select or type country"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3>Status</h3>
                <hr />
                <div className="form-grid center-step">
                  <div className="form-group">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      value={status}
                      onChange={setStatus}
                      placeholder="Select Status"
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused
                            ? "0 0 0px rgba(21, 21, 22, 0.4)"
                            : "none",
                          "&:hover": "none",
                          fontSize: "15px",
                          color: "black",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      className="note-textarea"
                      rows="8"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Enter note here"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && isSeniorAgent && (
              <div>
                <h3>Admin Areas</h3>
                <hr />

                {/* Currently Assigned Areas */}
                {assignedAreas.length > 0 && (
                  <div
                    style={{
                      marginBottom: "20px",
                      background: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "6px",
                    }}
                  >
                    <h4>Currently Assigned Areas ({assignedAreas.length})</h4>
                    <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {assignedAreas.map((area) => (
                        <div
                          key={area.admin_area_id}
                          style={{
                            padding: "8px",
                            margin: "5px 0",
                            background: "white",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <strong>{area.admin_name}</strong>
                          {area.county && (
                            <span style={{ color: "#666", marginLeft: "8px" }}>
                              ({area.county.county_name})
                            </span>
                          )}
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#888",
                              marginTop: "4px",
                            }}
                          >
                            Towns: {area.towns_count} | Assigned:{" "}
                            {new Date(area.assigned_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Filter - Simplified */}
                <div
                  style={{
                    marginBottom: "20px",
                    background: "#f5f5f5",
                    padding: "15px",
                    borderRadius: "6px",
                  }}
                >
                  <h4>Filter Available Areas</h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "10px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "14px",
                        }}
                      >
                        Search
                      </label>
                      <input
                        type="text"
                        value={areaFilters.search}
                        onChange={(e) =>
                          setAreaFilters({
                            ...areaFilters,
                            search: e.target.value,
                          })
                        }
                        placeholder="Search by area name..."
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Available Areas */}
                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <h4>Available Areas ({availableAreas.length})</h4>
                    <div>
                      <button
                        onClick={selectAllVisible}
                        style={{
                          marginRight: "10px",
                          background: "none",
                          border: "none",
                          color: "rgba(0, 0, 160, 1)",
                          cursor: "pointer",
                        }}
                      >
                        Select All
                      </button>
                      <button
                        onClick={clearSelections}
                        style={{
                          background: "none",
                          border: "none",
                          color: "rgba(0, 0, 160, 1)",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {loadingAreas ? (
                    <p>Loading...</p>
                  ) : availableAreas.length === 0 ? (
                    <p>No admin areas found</p>
                  ) : (
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "10px",
                      }}
                    >
                      {availableAreas.map((area) => (
                        <div
                          key={area.admin_area_id}
                          style={{
                            padding: "10px",
                            margin: "5px 0",
                            background: area.is_assigned
                              ? "#f5f5f5"
                              : selectedAreas.some(
                                    (a) =>
                                      a.admin_area_id === area.admin_area_id,
                                  )
                                ? "#e3f2fd"
                                : "white",
                            borderRadius: "4px",
                            border: `1px solid ${area.is_assigned ? "#ccc" : selectedAreas.some((a) => a.admin_area_id === area.admin_area_id) ? "rgba(0, 0, 160, 1)" : "#e0e0e0"}`,
                            cursor: area.is_assigned
                              ? "not-allowed"
                              : "pointer",
                            opacity: area.is_assigned ? 0.7 : 1,
                          }}
                          onClick={() =>
                            !area.is_assigned && toggleAreaSelection(area)
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <strong>{area.admin_name}</strong>
                              {area.county && (
                                <span
                                  style={{ color: "#666", marginLeft: "8px" }}
                                >
                                  ({area.county.county_name})
                                </span>
                              )}
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#888",
                                  marginTop: "4px",
                                }}
                              >
                                Towns: {area.towns_count}
                              </div>
                            </div>
                            {area.is_assigned && (
                              <span
                                style={{
                                  fontSize: "12px",
                                  background: "#4caf50",
                                  color: "white",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                }}
                              >
                                Already Assigned
                              </span>
                            )}
                            {!area.is_assigned &&
                              selectedAreas.some(
                                (a) => a.admin_area_id === area.admin_area_id,
                              ) && (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    background: "rgba(0, 0, 160, 1)",
                                    color: "white",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                  }}
                                >
                                  Selected
                                </span>
                              )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Options */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={replaceExisting}
                      onChange={(e) => setReplaceExisting(e.target.checked)}
                    />
                    <span>
                      Replace all existing assignments (removes unselected
                      areas)
                    </span>
                  </label>
                </div>

                {/* Selected Count */}
                {selectedAreas.length > 0 && (
                  <div
                    style={{
                      marginBottom: "10px",
                      color: "rgba(0, 0, 160, 1)",
                    }}
                  >
                    {selectedAreas.length} area(s) selected
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {step === 3 && (
              <button
                className="btn prev"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            )}

            {step > 1 && (
              <button
                className="btn prev"
                onClick={prevStep}
                disabled={loading}
              >
                Previous
              </button>
            )}

            {step < (isSeniorAgent ? 4 : 3) && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={loading || emailChecking || phoneChecking}
                style={{
                  opacity:
                    !loading && !emailChecking && !phoneChecking ? 1 : 0.6,
                  cursor:
                    !loading && !emailChecking && !phoneChecking
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                {emailChecking || phoneChecking ? "Checking..." : "Next"}
              </button>
            )}

            {step === 3 && !isSeniorAgent && (
              <button
                className="btn next"
                onClick={handleUpdate}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            )}

            {step === 4 && isSeniorAgent && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={loading || selectedAreas.length === 0}
              >
                {loading ? "Assigning..." : `Assign & Update`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditAgentProfile;
